// src/lib/billing.ts
// Couche d'abstraction paiement :
//  - Web            -> Stripe (Netlify functions, inchangé)
//  - iOS / Android   -> RevenueCat (StoreKit / Google Play Billing)
//
// L'entitlement fait foi côté serveur : `user_profiles.is_premium`, mis à jour
// par le webhook Stripe (web) OU le webhook RevenueCat (mobile).
// Ce module ne sert qu'à DÉCLENCHER l'achat natif et lire l'état local RC.

import { Capacitor } from "@capacitor/core";
import {
  Purchases,
  LOG_LEVEL,
  PURCHASES_ERROR_CODE,
  type PurchasesOffering,
  type PurchasesPackage,
  type CustomerInfo,
} from "@revenuecat/purchases-capacitor";

export const ENTITLEMENT_ID =
  process.env.REACT_APP_REVENUECAT_ENTITLEMENT || "premium";

const IOS_KEY = process.env.REACT_APP_REVENUECAT_IOS_KEY || "";
const ANDROID_KEY = process.env.REACT_APP_REVENUECAT_ANDROID_KEY || "";

export type BillingCycle = "monthly" | "yearly";

/** true seulement dans le wrapper natif Capacitor (iOS / Android). */
export function isNativeBilling(): boolean {
  return Capacitor.isNativePlatform();
}

let configured = false;
let configuring: Promise<void> | null = null;

/**
 * Configure le SDK RevenueCat (idempotent). No-op sur le web.
 * À appeler une fois au boot de l'app native.
 */
export async function initRevenueCat(): Promise<void> {
  if (!isNativeBilling() || configured) return;
  if (configuring) return configuring;

  const platform = Capacitor.getPlatform();
  const apiKey = platform === "ios" ? IOS_KEY : ANDROID_KEY;
  if (!apiKey) {
    console.warn(
      `[billing] Clé RevenueCat manquante pour ${platform} — achat natif désactivé.`
    );
    return;
  }

  configuring = (async () => {
    try {
      if (process.env.NODE_ENV !== "production") {
        await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
      }
      await Purchases.configure({ apiKey });
      configured = true;
    } catch (e) {
      console.error("[billing] configure RevenueCat a échoué:", e);
    } finally {
      configuring = null;
    }
  })();

  return configuring;
}

/** Lie l'utilisateur RevenueCat à l'id Supabase (= app_user_id côté webhook). */
export async function identifyRevenueCat(userId: string): Promise<void> {
  if (!isNativeBilling() || !userId) return;
  await initRevenueCat();
  if (!configured) return;
  try {
    await Purchases.logIn({ appUserID: userId });
  } catch (e) {
    console.error("[billing] logIn RevenueCat a échoué:", e);
  }
}

/** À appeler à la déconnexion Supabase. */
export async function logoutRevenueCat(): Promise<void> {
  if (!isNativeBilling() || !configured) return;
  try {
    await Purchases.logOut();
  } catch (e) {
    // logOut throw si l'utilisateur est déjà anonyme -> sans gravité
    console.warn("[billing] logOut RevenueCat:", e);
  }
}

/** Offering courant configuré dans le dashboard RevenueCat. */
export async function getCurrentOffering(): Promise<PurchasesOffering | null> {
  if (!isNativeBilling()) return null;
  await initRevenueCat();
  if (!configured) return null;
  const { current } = await Purchases.getOfferings();
  return current ?? null;
}

/** Renvoie le package correspondant au cycle demandé (mensuel / annuel). */
export function packageForCycle(
  offering: PurchasesOffering | null,
  cycle: BillingCycle
): PurchasesPackage | null {
  if (!offering) return null;
  const pkg = cycle === "yearly" ? offering.annual : offering.monthly;
  return pkg ?? null;
}

function hasEntitlement(info: CustomerInfo): boolean {
  return Boolean(info.entitlements.active[ENTITLEMENT_ID]);
}

export type PurchaseResult =
  | { ok: true; active: boolean }
  | { ok: false; cancelled: boolean; message: string };

/** Lance l'achat natif d'un package. */
export async function purchasePackage(
  pkg: PurchasesPackage
): Promise<PurchaseResult> {
  try {
    const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
    return { ok: true, active: hasEntitlement(customerInfo) };
  } catch (e: any) {
    const cancelled =
      e?.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR ||
      e?.userCancelled === true;
    return {
      ok: false,
      cancelled,
      message: e?.message || "Achat impossible.",
    };
  }
}

/** Restauration des achats (obligatoire sur iOS). */
export async function restorePurchases(): Promise<PurchaseResult> {
  try {
    const { customerInfo } = await Purchases.restorePurchases();
    return { ok: true, active: hasEntitlement(customerInfo) };
  } catch (e: any) {
    return {
      ok: false,
      cancelled: false,
      message: e?.message || "Restauration impossible.",
    };
  }
}

/** État de l'entitlement tel que connu localement par le SDK RC. */
export async function getNativeEntitlementActive(): Promise<boolean> {
  if (!isNativeBilling() || !configured) return false;
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    return hasEntitlement(customerInfo);
  } catch {
    return false;
  }
}

/**
 * Nom du store pertinent pour la plateforme courante, à afficher dans l'UI.
 * Important pour la 2.3.10 App Store : le binaire iOS ne doit jamais
 * mentionner Google Play (ni l'inverse côté Android).
 */
export function storeName(): string {
  const platform = Capacitor.getPlatform();
  if (platform === "ios") return "App Store";
  if (platform === "android") return "Google Play";
  return "App Store / Google Play";
}

/** Deep link vers la gestion d'abonnement du store (hors app). */
export function openStoreSubscriptions(): void {
  const url =
    Capacitor.getPlatform() === "ios"
      ? "itms-apps://apps.apple.com/account/subscriptions"
      : "https://play.google.com/store/account/subscriptions";
  window.open(url, "_system");
}
